import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  getInbox,
  getInboxMessage,
  getRecentInbox,
  getUnreadInboxCount,
  getInboxStats,
  markInboxRead,
  markAllInboxRead,
} from '@/services/api'
import type { InboxStats } from '@/services/api'
import { useAuthContext } from '@/components/auth/AuthProvider'
import { queryKeys } from '@/hooks/useQueryKeys'
import type { InboxMessage, PaginationParams } from '@/types'

/**
 * useInbox Hook
 * Provides paginated inbox messages with message type filter.
 */
export interface UseInboxParams extends PaginationParams {
  messageType?: 'alert' | 'system' | null
  searchQuery?: string
  readStatus?: 'all' | 'unread' | 'read'
}

export interface UseInboxReturn {
  messages: InboxMessage[]
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  error: Error | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  refetch: () => void
}

export function useInbox(params: UseInboxParams = {}): UseInboxReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null
  const { page = 1, limit = 20, messageType = null, searchQuery, readStatus } = params

  const query = useQuery({
    queryKey: queryKeys.inbox.list(userId, { page, limit, messageType, searchQuery, readStatus }),
    queryFn: () => getInbox({ page, limit, messageType, searchQuery, readStatus }),
    enabled: !!user,
    placeholderData: keepPreviousData,
  })

  const pagination = query.data?.pagination ?? { page, limit, total: 0 }

  return {
    messages: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    pagination: {
      ...pagination,
      totalPages: Math.ceil(pagination.total / pagination.limit),
    },
    refetch: query.refetch,
  }
}

/**
 * useInboxMessage Hook
 * Fetches a single inbox message by ID.
 */
export interface UseInboxMessageReturn {
  message: InboxMessage | null
  isLoading: boolean
  error: Error | null
}

export function useInboxMessage(id: string | undefined): UseInboxMessageReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: queryKeys.inbox.detail(userId, id ?? ''),
    queryFn: () => getInboxMessage(id!),
    enabled: !!user && !!id,
  })

  return {
    message: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * useRecentInbox Hook
 * Returns recent inbox items for bell dropdown (unread first).
 */
export interface UseRecentInboxReturn {
  messages: InboxMessage[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useRecentInbox(limit: number = 5): UseRecentInboxReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: queryKeys.inbox.recent(userId, limit),
    queryFn: () => getRecentInbox(limit),
    enabled: !!user,
    staleTime: 30000, // 30s stale time
  })

  return {
    messages: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * useUnreadInboxCount Hook
 * Returns the count of unread inbox messages for the badge.
 */
export interface UseUnreadInboxCountReturn {
  count: number
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useUnreadInboxCount(): UseUnreadInboxCountReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: queryKeys.inbox.unreadCount(userId),
    queryFn: getUnreadInboxCount,
    refetchInterval: 60000, // Poll every 60s
    enabled: !!user,
  })

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * useInboxStats Hook
 * Returns inbox message counts for the last 24h, 7d, 30d.
 */
export interface UseInboxStatsReturn {
  stats: InboxStats
  isLoading: boolean
  error: Error | null
}

export function useInboxStats(): UseInboxStatsReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: queryKeys.inbox.stats(userId),
    queryFn: getInboxStats,
    enabled: !!user,
    staleTime: 60000, // 60s stale time (matches unread count polling)
  })

  return {
    stats: query.data ?? { '24h': 0, '7d': 0, '30d': 0 },
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * useMarkInboxRead Hook
 * Mutation for marking a single inbox message as read.
 */
export interface UseMarkInboxReadReturn {
  markAsRead: (inboxId: string) => Promise<void>
  isPending: boolean
  error: Error | null
}

export function useMarkInboxRead(): UseMarkInboxReadReturn {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const mutation = useMutation({
    mutationFn: markInboxRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all(userId) })
    },
  })

  return {
    markAsRead: async (inboxId: string) => {
      await mutation.mutateAsync(inboxId)
    },
    isPending: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * useMarkAllInboxRead Hook
 * Mutation for marking all inbox messages as read.
 */
export interface UseMarkAllInboxReadReturn {
  markAllAsRead: () => Promise<void>
  isPending: boolean
  error: Error | null
}

export function useMarkAllInboxRead(): UseMarkAllInboxReadReturn {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const mutation = useMutation({
    mutationFn: markAllInboxRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all(userId) })
    },
  })

  return {
    markAllAsRead: async () => {
      await mutation.mutateAsync()
    },
    isPending: mutation.isPending,
    error: mutation.error,
  }
}
